import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Modal } from 'react-bootstrap';
import TabView from '../../../../components/ui/TabView';
import OutreachContacts from './OutreachContacts';
import OutreachMemberNeeds from './OutreachMemberNeeds';
import OutreachMemberInfo from './OutreachMemberInfo';
import {
    checkObjectsEqual,
    checkValuesEqual, encodeSpecialChars,
    isObjectEmpty, pickProps,
    prefixObjectKeys,
    removeHyphens, removePrefixFromObjectKeys,
    getCurrentDate,
    toFormDateTime,
} from '../../../../utils/utils';
import {
    getProxyGuardianMemberById,
    resetMemberComments,
    UpdateRecruitmentOutreachMemberData, insertMemberComment,
    getCaregiverDetailsByMemberId, InsertCareGivers, UpdateCareGivers,
    resetCareGiverDetails, getMemberComments, resetProxyMemberData,
} from '../../../../ducks/advisoryGroup';
import cohortApi from '../../../../services/api/cohortService';
import {
    getCohortMemberIdFromAgRow,
    hasMeaningfulApplicationId,
    isAugmentedPlaceholderMember,
    mergeCohortMemberIntoAgOutreachRow,
} from '../../../../utils/recruitmentAugmentedMember';
import { AppLoadContext } from '../../../../components/ui/AppLoadContext';
import { AlertContext } from '../../../../context/AlertContext';
import messages from '../../../../utils/helper/messages';
import {
    ALERT_TYPE, CARE_GIVER_MEMBER_STATUS, PROXY_FLAG, AG_RECRUITMENT_MEMBER_STATUS,
    MEMBER_OUTCOME_STATUS, RECORD_STATUS_ABBR, LANGUAGE_TYPE, MEMBER_ACCOMODATION_NEEDS, YES_NO_INVERSE,
    ONBOARDING_STATUS
} from '../../../../constants';
import Loader from '../../../../components/ui/Loader';
import styles from './OutreachDetailsModal.module.scss';


const OutreachDetailsModal = ({ show, data, info, onCancel, onEdit, cohortId = null }) => {
    const isAppLoaded = useContext(AppLoadContext);
    const { showAlert } = useContext(AlertContext);

    const dispatch = useDispatch();
    const proxyDetails = useSelector(state => state.advisoryGroup?.proxyAttendeeDetails);
    const caregiverDetails = useSelector(state => state.advisoryGroup?.caregiverDetails);
    const currentUser = useSelector(state => state.user?.currentUser);
    const pickList = useSelector((state) => state.company?.pickList);

    const [memberData, setMemberData] = useState(null);
    const [proxyData, setProxyData] = useState(null);
    const [careGiverData, setCareGiverData] = useState([]);
    const [newCareGiverData, setNewCareGiverData] = useState([]);
    const [emergencyContactData, setEmergencyContactData] = useState(null);
    const [isSubmitDisable, setIsSubmitDisable] = useState(true);
    const [noPhoneChecked, setNoPhoneChecked] = useState(false);

    const initialFormValidState = {
        outreach: true,
        member: true,
        memberNeeds: true,
        proxy: true,
        emergencyContact: true,
        careGiver: true,
        careGiverNew: true,
    }
    const [isFormValid, setIsFormValid] = useState(initialFormValidState);
    const [isFormDataEqual, setIsFormDataEqual] = useState({
        member: true,
        proxy: true,
        emergencyContact: true,
        careGiver: true,
        careGiverNew: true,
    });

    const [activeTab, setActiveTab] = useState('member-info');
    const [activeAccordion, setActiveAccordion] = useState(null);
    const [outreachAttemptLastDate, setOutreachAttemptLastDate] = useState(null);
    const [isDirty, setIsDirty] = useState({ member: false, proxy: false, careGiver: false, careGiverNew: false });
    const [initialData, setInitialData] = useState({
        memberData: null,
        proxyData: null,
        emergencyContactData: null,
        careGiverData: [],
        newCareGiverData: [],
    });

    const [cohortMemberFetch, setCohortMemberFetch] = useState({ status: 'idle', member: null });
    const [submitInProgress, setSubmitInProgress] = useState(false);
    const saveOpsPendingRef = useRef(0);

    const hydratedData = useMemo(() => {
        if (!data) return null;
        if (!isAugmentedPlaceholderMember(data)) return data;

        const preHydrated =
            data.applicationFirstName ||
            data.applicationLastName ||
            data.firstName ||
            data.lastName;
        if (preHydrated) return data;

        if (cohortMemberFetch.status === 'idle' || cohortMemberFetch.status === 'loading') return null;
        if (cohortMemberFetch.status === 'success' && cohortMemberFetch.member) {
            return mergeCohortMemberIntoAgOutreachRow(data, cohortMemberFetch.member);
        }
        return data;
    }, [data, cohortMemberFetch]);

    const tabChanged = (e) => {
        setActiveTab(e);
    }

    const handleCancel = () => {
        saveOpsPendingRef.current = 0;
        setSubmitInProgress(false);
        dispatch(resetCareGiverDetails());
        dispatch(resetMemberComments());
        dispatch(resetProxyMemberData());
        setCohortMemberFetch({ status: 'idle', member: null });
        setMemberData(null);
        setProxyData(null);
        setCareGiverData([]);
        setNewCareGiverData([]);
        setEmergencyContactData(null);
        setIsSubmitDisable(true);
        setIsFormValid(initialFormValidState);
        onCancel();
    }

    const handleFormValidation = (value, key) => {
        const outreachFields = ['outcome', 'callDisposition', 'memberCommunicationLastDate', 'empaneledDate'];
        let updatedFormValid = { ...isFormValid };
        if (activeTab === 'member-info') {
            if (outreachFields.includes(key)) {
                updatedFormValid.outreach = value;
            } else {
                updatedFormValid.member = value;
            }
        } else if (activeTab === 'member-needs') {
            updatedFormValid.memberNeeds = value;
        } else if (activeTab === 'contacts') {
            if (activeAccordion === 'proxy') {
                updatedFormValid.proxy = value;
            } else if (activeAccordion === 'emergencyContact') {
                updatedFormValid.emergencyContact = value;
            } else if (activeAccordion?.includes('careGiver')) {
                if (activeAccordion.includes('new_')) {
                    updatedFormValid.careGiverNew = value;
                } else {
                    updatedFormValid.careGiver = value;
                }
            }
        }

        setIsFormValid(updatedFormValid);
    }

    const handleCareGiverDataChange = (key, value, isPhoneEmpty) => {
        if (activeAccordion.includes('careGiver_new_')) {
            setIsDirty(prevState => ({
                ...prevState,
                careGiverNew: true
            }));
            setNewCareGiverData((prevState) => {
                if (prevState.length === 0) {
                    return [{ [key]: isPhoneEmpty ? null : value }];
                }
                return prevState.map(cg => {
                    const cgId = Object.keys(cg)[0]?.split('_')[1];
                    if (key.includes(cgId)) {
                        return { ...cg, [key]: isPhoneEmpty ? null : value };
                    }
                    return cg;
                });
            })
        } else {
            setIsDirty(prevState => ({
                ...prevState,
                careGiver: true
            }))
            setCareGiverData((prevData) => {
                return prevData.map(cg => {
                    const cgId = Object.keys(cg)[0]?.split('_')[0];
                    if (key.includes(cgId)) {
                        return { ...cg, [key]: isPhoneEmpty ? null : value };
                    }
                    return cg;
                });
            });
        }
    }

    const handleDeleteComment = () => {
        dispatch(getMemberComments(data.id));
    }

    const handleInputChange = (key, value) => {
        const inputField = document.querySelector(`[name="${key}"]`);
        let inputValue = inputField?.type === 'tel' ? removeHyphens(value) : value;
        if (key === 'callDisposition') {
            inputValue = inputValue === null ? 0 : Number(inputValue);
        }
        if (key === 'noPhone') {
            const isChecked = Array.isArray(value) && value.length > 0;
            setNoPhoneChecked(isChecked);
            setMemberData((prevData) => ({
                ...prevData,
                noPhone: value,
                applicationPhone: isChecked ? null : memberData?.applicationPhone,
                applicationPhoneType: isChecked ? null : memberData?.applicationPhoneType,
            }));
        }
        const isPhoneEmpty = inputField?.type === 'tel' && inputValue === '';

        const isMemberField = Object.keys(initialData.memberData).includes(key);
        const isProxyField = Object.keys(initialData.proxyData).includes(key);
        const isEmergencyContactField = Object.keys(initialData.emergencyContactData).includes(key);

        let isMemberDataEqual, isProxyDataEqual, isEmergencyContactDataEqual, isCaregiverDataEqual, isNewCaregiverDataEqual;

        let updatedMemberData = {};
        if (isMemberField) {
            updatedMemberData = { ...memberData, [key]: isPhoneEmpty ? null : inputValue };
            if (key === 'outcome' || key === 'callDisposition') {
                const {
                    outcome: initialOutcome,
                    callDisposition: initialCallDisposition,
                    memberCommunicationLastDate: initialMemberCommunicationLastDate,
                    empaneledDate: initialEmpaneledDate,
                } = initialData.memberData;

                // compare both fields, regardless of which triggered the change
                const hasChanged = (updatedMemberData.outcome !== initialOutcome) || (updatedMemberData.callDisposition !== initialCallDisposition);
                const isOutcomeReverted = updatedMemberData.outcome === initialOutcome;
                const isOutcomeNotAcceptance = updatedMemberData.outcome !== MEMBER_OUTCOME_STATUS.Acceptance;

                // if outcome value is changed to 'Acceptance', set member startdate to current date (US-8787)
                if (key === 'outcome' && inputValue === MEMBER_OUTCOME_STATUS.Acceptance) {
                    updatedMemberData.empaneledDate = getCurrentDate();
                } else if (isOutcomeReverted || isOutcomeNotAcceptance) { // If outcome is reverted, or outcome is not Acceptance, reset the empaneled date
                    updatedMemberData.empaneledDate = initialEmpaneledDate;
                }

                updatedMemberData.memberCommunicationLastDate = hasChanged ? getCurrentDate() : initialMemberCommunicationLastDate;
            }
            isMemberDataEqual = checkObjectsEqual(initialData.memberData, updatedMemberData);
        } else {
            isMemberDataEqual = checkObjectsEqual(initialData.memberData, memberData);
        }

        if (!isMemberDataEqual && ['id', 'outcome', 'outreachAttemptLastDate', 'callDisposition', 'memberCommunicationLastDate'].includes(key)) {
            setOutreachAttemptLastDate(new Date());
        }

        if (isProxyField) {
            isProxyDataEqual = checkObjectsEqual(initialData.proxyData, { ...proxyData, [key]: isPhoneEmpty ? null : inputValue });
        } else {
            isProxyDataEqual = checkObjectsEqual(initialData.proxyData, proxyData);
        }

        if (isEmergencyContactField) {
            isEmergencyContactDataEqual = checkObjectsEqual(initialData.emergencyContactData, { ...emergencyContactData, [key]: isPhoneEmpty ? null : inputValue });
        } else {
            isEmergencyContactDataEqual = checkObjectsEqual(initialData.emergencyContactData, emergencyContactData);
        }

        // Caregiver
        const currentId = key?.split('_')[0];
        isCaregiverDataEqual = careGiverData.every((cg) => {
            const idKey = Object.keys(cg).find(key => key.includes('_id'));
            const initialCg = initialData.careGiverData.find(cgData => Object.keys(cgData).includes(idKey));
            const cgId = Object.keys(cg)[0]?.split('_')[0];
            let isEqual;

            const isCareGiverField = Object.keys(cg).includes(key);

            if (cgId === currentId && isCareGiverField) {
                isEqual = checkObjectsEqual(initialCg, { ...cg, [key]: isPhoneEmpty ? null : inputValue });
            } else {
                isEqual = checkObjectsEqual(initialCg, cg);
            }

            return isEqual;
        });

        // New Caregiver
        isNewCaregiverDataEqual = (initialData.newCareGiverData.length === newCareGiverData.length) || (newCareGiverData.length === 1 && isObjectEmpty(newCareGiverData[0]));

        let updatedIsFormDataEqual = {
            ...isFormDataEqual,
            member: isMemberDataEqual,
            proxy: isProxyDataEqual,
            emergencyContact: isEmergencyContactDataEqual,
            careGiver: isCaregiverDataEqual,
            careGiverNew: isNewCaregiverDataEqual,
        }

        if (activeTab === 'contacts') {
            if (activeAccordion === 'proxy') {
                setIsDirty(prevState => ({
                    ...prevState,
                    proxy: true
                }))
                setProxyData((prevData) => ({
                    ...prevData,
                    [key]: isPhoneEmpty ? null : inputValue
                }));
            } else if (activeAccordion === 'emergencyContact') {
                setIsDirty(prevState => ({
                    ...prevState,
                    member: true
                }));
                setEmergencyContactData((prevData) => ({
                    ...prevData,
                    [key]: isPhoneEmpty ? null : inputValue
                }));
            } else if (activeAccordion?.includes('careGiver')) {
                handleCareGiverDataChange(key, inputValue, isPhoneEmpty);
            }
        } else {
            setIsDirty(prevState => ({
                ...prevState,
                member: true
            }));

            setMemberData(prevData => ({
                ...prevData,
                ...updatedMemberData
            }));
        }

        setIsFormDataEqual(updatedIsFormDataEqual);
    }

    const getFormDisabledState = (validObj, equalObj) => {
        let disableFlag;
        const equalFlag = Object.values(equalObj).every(value => value === true);
        const validFlag = Object.values(validObj).every(value => value === true);
        if (!validFlag) {
            disableFlag = true;
        } else {
            disableFlag = equalFlag
        }

        return disableFlag;
    }

    const completeSaveOp = () => {
        saveOpsPendingRef.current = Math.max(0, saveOpsPendingRef.current - 1);
        if (saveOpsPendingRef.current === 0) {
            setSubmitInProgress(false);
        }
    };

    const handleSuccess = () => {
        completeSaveOp();
        setIsSubmitDisable(false);
        showAlert(messages.success.updateOutreachDetails, ALERT_TYPE.SUCCESS);
        onCancel();
        onEdit();
        dispatch(resetMemberComments());
    };

    const handleError = (error) => {
        completeSaveOp();
        setIsSubmitDisable(false);
        const msg =
            typeof error === 'string'
                ? error
                : error?.message || error?.error || 'Request failed';
        showAlert(`${messages.error.prefix} ${msg}`, ALERT_TYPE.ERROR);
    };

    const handleCommentSuccess = () => {
        completeSaveOp();
        setIsSubmitDisable(false);
        dispatch(resetMemberComments());
    };

    const getMemberStatus = (memberData) => {
        if (memberData?.outcome === MEMBER_OUTCOME_STATUS.Acceptance) {
            return AG_RECRUITMENT_MEMBER_STATUS.Enrolled;
        } else if (memberData?.outcome === MEMBER_OUTCOME_STATUS.Declined) {
            return AG_RECRUITMENT_MEMBER_STATUS.Decline;
        } else {
            return data.memberStatus;
        }
    }

    const handleSubmit = () => {
        setIsSubmitDisable(true);

        const submitProxyData = {
            memberId: data.id,
            proxyGuardianFlag: PROXY_FLAG.Proxy,
            proxyFirstName: proxyData?.proxyFirstName,
            proxyLastName: proxyData?.proxyLastName,
            proxyAddress1: proxyData?.proxyAddress1,
            proxyAddress2: proxyData?.proxyAddress2,
            proxyCity: proxyData?.proxyCity,
            proxyState: proxyData?.proxyState,
            proxyPostalCode: proxyData?.proxyPostalCode,
            proxyEmail: proxyData?.proxyEmail,
            proxyPhone: removeHyphens(proxyData?.proxyPhone),
        }

        let submitCareGiverData = [], submitNewCareGiverData = [];
        for (const cg of careGiverData) {
            const cgId = Object.keys(cg)[0]?.split('_')[0];
            const updatedCg = removePrefixFromObjectKeys(cg, `${cgId}_careGiver_`);
            const submitCareGiver = {
                id: cgId,
                memberId: data.id,
                companyId: data.companyId,
                firstName: updatedCg.firstName,
                lastName: updatedCg.lastName,
                email: updatedCg.email,
                phone: removeHyphens(updatedCg.phone),
                phoneType: updatedCg.phoneType ?? null,
                hipaaFormFlag: updatedCg.hipaaFormFlag,
                memberFlag: updatedCg.memberFlag,
                guardianFlag: updatedCg.guardianFlag ?? null,
                docCode: updatedCg.docCode,
                comment: encodeSpecialChars(updatedCg.comment),
                lastUserToUpdate: currentUser.id,
            };
            submitCareGiverData.push(submitCareGiver);
        }

        for (const cg of newCareGiverData) {
            const cgIndex = Object.keys(cg)[0]?.split('_')[1];
            const updatedCg = removePrefixFromObjectKeys(cg, `new_${cgIndex}_careGiver_`);
            const submitNewCareGiver = {
                agId: info.agId,
                memberId: data.id,
                companyId: data.companyId,
                firstName: updatedCg.firstName,
                lastName: updatedCg.lastName,
                email: updatedCg.email ?? null,
                phone: removeHyphens(updatedCg.phone) ?? null,
                phoneType: updatedCg.phoneType ?? null,
                hipaaFormFlag: updatedCg.hipaaFormFlag ?? null,
                memberFlag: updatedCg.memberFlag ?? null,
                guardianFlag: updatedCg.guardianFlag ?? null,
                docCode: updatedCg.docCode ?? null,
                comment: encodeSpecialChars(updatedCg.comment),
                lastUserToUpdate: currentUser.id,
                recordStatus: CARE_GIVER_MEMBER_STATUS.Operational,
            };

            submitNewCareGiverData.push(submitNewCareGiver);
        }

        let saveOpCount = 0;
        if (submitCareGiverData?.length && isDirty.careGiver) saveOpCount++;
        if (submitNewCareGiverData?.length && isDirty.careGiverNew) saveOpCount++;
        if (!isObjectEmpty(data) && (isDirty.member || isDirty.proxy)) saveOpCount++;
        if (memberData.comment && !checkValuesEqual(initialData.memberData.comment, memberData.comment)) {
            saveOpCount++;
        }

        if (saveOpCount === 0) {
            setIsSubmitDisable(false);
            return;
        }

        saveOpsPendingRef.current = saveOpCount;
        setSubmitInProgress(true);

        if (submitCareGiverData?.length && isDirty.careGiver) {
            dispatch(UpdateCareGivers(submitCareGiverData, handleSuccess, handleError));
        }

        if (submitNewCareGiverData?.length && isDirty.careGiverNew) {
            dispatch(InsertCareGivers(submitNewCareGiverData, handleSuccess, handleError));
        }

        const submitEmergencyContactData = {
            memberId: data.id,
            emergencyFirstName: emergencyContactData?.emergencyFirstName,
            emergencyLastName: emergencyContactData?.emergencyLastName,
            phoneEmergency: removeHyphens(emergencyContactData?.phoneEmergency),
            phoneTypeEmergency: emergencyContactData?.phoneTypeEmergency,
            relationshipEmergency: emergencyContactData?.relationshipEmergency,
        }

        if (!isObjectEmpty(data) && (isDirty.member || isDirty.proxy)) {
            const outreachBody = {
                ...memberData,
                ...submitEmergencyContactData,
                ...submitProxyData,
                applicationPhone: noPhoneChecked ? 'xxxxxxxxxx' : removeHyphens(memberData?.applicationPhone),
                applicationPhoneType: noPhoneChecked ? null : memberData.applicationPhoneType,
                agId: info.agId,
                memberStatus: getMemberStatus(memberData),
                applicationPrimaryLanguage: memberData?.applicationPrimaryLanguage === LANGUAGE_TYPE.Other ? memberData?.primaryLanguageOther : memberData?.applicationPrimaryLanguage,
                specificAccommodationNeeds: (memberData?.specificAccommodationNeeds === MEMBER_ACCOMODATION_NEEDS.Other && memberData?.specifyOtherAccommodation) ? memberData?.specifyOtherAccommodation : memberData?.specificAccommodationNeeds,
                id: data.id,
                companyId: data.companyId,
                empaneledDate: memberData?.outcome === MEMBER_OUTCOME_STATUS.Acceptance ? new Date() : data.empaneledDate,
                onboarding: memberData?.outcome === MEMBER_OUTCOME_STATUS.Acceptance ? ONBOARDING_STATUS.Pending : data.onboarding,
                memberRetireDate: data.memberRetireDate,
                retirementReason: data.retirementReason,
                callDisposition: memberData.callDisposition !== 0 ? memberData.callDisposition : null,
                applicationDate: data.applicationDate,
                outreachAttemptLastDate: outreachAttemptLastDate ? outreachAttemptLastDate : data.outreachAttemptLastDate,
                lastUsertoUpdateRecord: currentUser.id,
            };

            const augmentedFirstOutreachSave =
                isAugmentedPlaceholderMember(data) && !hasMeaningfulApplicationId(data.applicationId);
            if (augmentedFirstOutreachSave) {
                delete outreachBody.applicationId;
            } else {
                outreachBody.applicationId = data.applicationId;
            }

            dispatch(UpdateRecruitmentOutreachMemberData(outreachBody, handleSuccess, handleError));
        }

        const commentData = {
            agId: info.agId,
            status: RECORD_STATUS_ABBR.Operational,
            companyId: currentUser.companyId,
            agMemberId: data.id,
            userId: currentUser.id,
            comment: encodeSpecialChars(memberData.comment),
        };

        if (memberData.comment && !checkValuesEqual(initialData.memberData.comment, memberData.comment)) {
            dispatch(insertMemberComment(commentData, handleCommentSuccess, handleError));
        }
    }

    const handleAccordionChange = (accordionKey) => {
        setActiveAccordion(accordionKey);
    }

    const tabsInfo = [
        {
            title: 'Member Info',
            eventKey: 'member-info',
            child: <OutreachMemberInfo
                data={memberData}
                onInputChange={handleInputChange}
                onDeleteComment={handleDeleteComment}
                onFormValidate={handleFormValidation}
                noPhoneChecked={noPhoneChecked}
            />
        },
        {
            title: 'Member Needs',
            eventKey: 'member-needs',
            child: <OutreachMemberNeeds
                data={memberData}
                onInputChange={handleInputChange}
                onFormValidate={handleFormValidation} />
        },
        {
            title: 'Contacts',
            eventKey: 'contacts',
            child: <OutreachContacts
                data={memberData}
                emergencyContact={emergencyContactData}
                proxy={proxyData}
                careGivers={careGiverData}
                newCareGivers={newCareGiverData}
                onInputChange={handleInputChange}
                onAccordionChange={handleAccordionChange}
                onFormValidate={handleFormValidation}
            />
        }
    ];

    useEffect(() => {
        if (isAppLoaded && data?.id) {
            dispatch(getProxyGuardianMemberById(data.id));
            dispatch(getCaregiverDetailsByMemberId(data.id));
        }
    }, [dispatch, isAppLoaded, data?.id]);

    useEffect(() => {
        if (!show || !data?.id) return;

        if (!isAugmentedPlaceholderMember(data)) {
            setCohortMemberFetch({ status: 'skipped', member: null });
            return;
        }

        const preHydrated =
            data.applicationFirstName ||
            data.applicationLastName ||
            data.firstName ||
            data.lastName;
        if (preHydrated) {
            setCohortMemberFetch({ status: 'skipped', member: null });
            return;
        }

        const cohortMemberId = getCohortMemberIdFromAgRow(data);
        if (!cohortId || cohortMemberId == null) {
            setCohortMemberFetch({ status: 'error', member: null });
            return;
        }

        let cancelled = false;
        setCohortMemberFetch({ status: 'loading', member: null });
        cohortApi
            .getCohortMemberById(cohortId, cohortMemberId)
            .then((res) => {
                if (cancelled) return;
                setCohortMemberFetch({ status: 'success', member: res.data });
            })
            .catch(() => {
                if (cancelled) return;
                setCohortMemberFetch({ status: 'error', member: null });
            });
        return () => {
            cancelled = true;
        };
    }, [show, data?.id, data?.memberId, data?.applicationId, data?.applicationFirstName, data?.firstName, cohortId]);

    useEffect(() => {
        if (hydratedData) {
            const memberDataFields = [
                // Member Info
                'outcome', 'callDisposition', 'memberCommunicationLastDate', 'participateReason', 'applicationFirstName', 'applicationLastName',
                'applicationHealthPlanId', 'applicationBirthday', 'applicationAddress', 'applicationAddress2', 'applicationCity', 'applicationState',
                'applicationPostalCode', 'applicationPhone', 'applicationPhoneType', 'applicationEmail', 'applicationId', 'comment', 'empaneledDate',
                'applicationPrimaryLanguage', 'needTranslator', 'primaryLanguageOther', 'deviceWithInternetAccess', 'deviceWithInternetAccessComment',
                // Member Needs
                'needTransportation', 'transportationNeeds', 'needTransportationComment', 'specificAccommodation', 'specificAccommodationNeeds', 'dietaryRestrictionsAccommodation', 'dietaryRestrictionsNeeds'
            ];
            const emergencyContactFields = ['emergencyFirstName', 'emergencyLastName', 'phoneEmergency', 'phoneTypeEmergency', 'relationshipEmergency'];

            const memberData = pickProps(hydratedData, memberDataFields);
            const emergencyData = pickProps(hydratedData, emergencyContactFields);
            const isSpecificOtherAccommodation = hydratedData?.specificAccommodationNeeds != null && !Object.keys(MEMBER_ACCOMODATION_NEEDS).includes(hydratedData.specificAccommodationNeeds);
            const hasNoPhone = hydratedData?.applicationPhone === 'xxxxxxxxxx';
            const languageOptions = pickList?.[0]?.languageList?.split(',').map((l) => l.trim()) || Object.keys(LANGUAGE_TYPE);
            const isCustomLanguage = hydratedData.applicationPrimaryLanguage && !languageOptions?.includes(hydratedData.applicationPrimaryLanguage);

            const normalizedMemberData = {
                ...memberData,
                noPhone: hasNoPhone ? ['noPhone'] : [],
                applicationPhone: hasNoPhone ? null : hydratedData?.applicationPhone,
                applicationPhoneType: hasNoPhone ? null : hydratedData?.applicationPhoneType,
                memberCommunicationLastDate: toFormDateTime(memberData.memberCommunicationLastDate),
                applicationBirthday: toFormDateTime(memberData.applicationBirthday),
                empaneledDate: toFormDateTime(memberData.empaneledDate),
                applicationPrimaryLanguage: isCustomLanguage ? LANGUAGE_TYPE.Other : hydratedData.applicationPrimaryLanguage,
                primaryLanguageOther: isCustomLanguage ? hydratedData.applicationPrimaryLanguage : '',
                specificAccommodationNeeds: isSpecificOtherAccommodation ? MEMBER_ACCOMODATION_NEEDS.Other : memberData.specificAccommodationNeeds,
                specifyOtherAccommodation: isSpecificOtherAccommodation ? hydratedData.specificAccommodationNeeds : ''
            };
            setNoPhoneChecked(hasNoPhone);

            setEmergencyContactData(emergencyData);
            setMemberData(normalizedMemberData);
            setInitialData((prevState) => ({
                ...prevState,
                memberData: normalizedMemberData,
                emergencyContactData: emergencyData,
                specificAccommodationNeeds: isSpecificOtherAccommodation ? MEMBER_ACCOMODATION_NEEDS.Other : memberData.specificAccommodationNeeds,
                specifyOtherAccommodation: isSpecificOtherAccommodation ? hydratedData.specificAccommodationNeeds : ''
            }));
        }
    }, [hydratedData, pickList]);


    useEffect(() => {
        if (proxyDetails) {
            const proxyFields = ['proxyFirstName', 'proxyLastName', 'proxyPhone', 'proxyAddress1', 'proxyAddress2', 'proxyCity', 'proxyState', 'proxyPostalCode', 'proxyEmail'];
            const proxyData = pickProps(proxyDetails, proxyFields);
            setProxyData(proxyData);
            setInitialData((prevState) => ({
                ...prevState,
                proxyData: proxyData
            }));
        }
    }, [proxyDetails]);

    useEffect(() => {
        if (caregiverDetails?.length > 0) {
            const prefixedCareGiverDetails = caregiverDetails.map(cg => prefixObjectKeys(cg, `${cg.id}_careGiver_`));
            setCareGiverData(prefixedCareGiverDetails);
            setInitialData((prevState) => ({
                ...prevState,
                careGiverData: prefixedCareGiverDetails
            }));
        }
    }, [caregiverDetails]);

    useEffect(() => {
        const disableFlag = getFormDisabledState(isFormValid, isFormDataEqual);
        setIsSubmitDisable(disableFlag);
    }, [isFormValid, isFormDataEqual]);

    useEffect(() => {
        setMemberData(prev => {
            let updatedData = { ...prev };
            let changed = false;

            if (memberData?.specificAccommodation === YES_NO_INVERSE.No) {
                updatedData.specificAccommodationNeeds = null;
                updatedData.specifyOtherAccommodation = '';
                changed = true;
            }
            if (memberData?.needTransportation === YES_NO_INVERSE.No) {
                updatedData.transportationNeeds = null;
                changed = true;
            }
            if (memberData?.dietaryRestrictionsAccommodation === YES_NO_INVERSE.No) {
                updatedData.dietaryRestrictionsNeeds = null;
                changed = true;
            }
            if (memberData?.deviceWithInternetAccess === YES_NO_INVERSE.Yes) {
                updatedData.deviceWithInternetAccessComment = null;
                changed = true;
            }

            return changed ? updatedData : prev;
        });
    }, [memberData?.specificAccommodation, memberData?.needTransportation, memberData?.dietaryRestrictionsAccommodation, memberData?.deviceWithInternetAccess]);


    return (
        <Modal
            show={show}
            fullscreen={false}
            backdrop="static"
            onHide={handleCancel}
            size="md">
            <Modal.Header closeButton>
                <Modal.Title>Outreach Info</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {cohortMemberFetch.status === 'error' && data && isAugmentedPlaceholderMember(data) && (
                    <Alert variant="warning" className="mb-3">
                        {messages.error.cohortDemographicsLoadFailed}
                    </Alert>
                )}
                {memberData === null || proxyData === null || emergencyContactData === null ?
                    <div className={`d-flex justify-content-center align-items-center w-100 ${styles.loaderWrap}`}>
                        <Loader />
                    </div> :
                    <TabView
                        tabsInfo={tabsInfo}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        onTabChange={tabChanged}
                        isValidated={true}
                        isSubmitDisable={isSubmitDisable}
                        submitInProgress={submitInProgress}
                    />
                }
            </Modal.Body>
        </Modal>
    );
};

export default OutreachDetailsModal;
